// src/scenes/sitemanager/DashboardFinance.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";

// Helper to map a YYYY-MM to a school year label, e.g. "2025-06" → "2025-26"
function getSchoolYear(yearMonth) {
    const [yStr, mStr] = yearMonth.split("-");
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    if (m >= 7) {
        // July or later: school year runs y–(y+1)
        return `${y}-${(y + 1).toString().slice(-2)}`;
    } else {
        // Before July: school year runs (y-1)–y
        return `${y - 1}-${y.toString().slice(-2)}`;
    }
}

export default function DashboardFinance() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth();

    // --- filters ---
    const [siteFilter, setSiteFilter] = useState(allocations[0]?.SchoolId || "");
    const [yearFilter, setYearFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    // --- raw data from your two views ---
    const [incData, setIncData] = useState([]);
    const [expData, setExpData] = useState([]);

    // --- dropdown options ---
    const [sites, setSites] = useState([]);
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    useEffect(() => {
        Promise.all([
            axios.get(
                "https://figazuresql.swancreate.com/api/swa/vw_KPI_IncomePerMEQBySiteMonth"
            ),
            axios.get(
                "https://figazuresql.swancreate.com/api/swa/vw_KPI_ExpensePerMEQBySiteMonth"
            ),
        ]).then(([incRes, expRes]) => {
            const inc = incRes.data;
            const exp = expRes.data;

            // build site list
            const siteMap = {};
            inc.concat(exp).forEach((r) => {
                if (
                    !isSchoolUser ||
                    allocations.some((a) => a.SchoolId === r.SchoolId)
                ) {
                    siteMap[r.SchoolId] = r.SchoolName;
                }
            });
            setSites(
                Object.entries(siteMap).map(([id, name]) => ({ id, name }))
            );
            if (!siteFilter && Object.keys(siteMap).length) {
                setSiteFilter(Object.keys(siteMap)[0]);
            }

            // year list by school year label
            const yearSet = new Set();
            inc.forEach((r) => {
                yearSet.add(getSchoolYear(r.YearMonth));
            });
            const sortedYears = Array.from(yearSet).sort();
            setYears(sortedYears);
            if (!yearFilter && sortedYears.length) {
                setYearFilter(sortedYears[0]);
            }

            // months list uses raw YYYY-MM values
            const mSet = new Set();
            inc.forEach((r) => mSet.add(r.YearMonth));
            setMonths(Array.from(mSet).sort());

            setIncData(inc);
            setExpData(exp);
        });
    }, [allocations, isSchoolUser]);

    // --- filtered slices ---
    const filteredInc = useMemo(
        () =>
            incData.filter(
                (r) =>
                    r.SchoolId === siteFilter &&
                    getSchoolYear(r.YearMonth) === yearFilter &&
                    (!monthFilter || r.YearMonth === monthFilter)
            ),
        [incData, siteFilter, yearFilter, monthFilter]
    );
    const filteredExp = useMemo(
        () =>
            expData.filter(
                (r) =>
                    r.SchoolId === siteFilter &&
                    getSchoolYear(r.YearMonth) === yearFilter &&
                    (!monthFilter || r.YearMonth === monthFilter)
            ),
        [expData, siteFilter, yearFilter, monthFilter]
    );

    // --- totals ---
    const totalIncome = filteredInc.reduce(
        (sum, r) => sum + (r.TotalIncomes || 0),
        0
    );
    const totalExpense = filteredExp.reduce(
        (sum, r) => sum + (r.TotalExpenses || 0),
        0
    );

    // --- leaderboards ---
    const incLeaderboard = useMemo(() => {
        const map = {};
        incData
            .filter((r) => getSchoolYear(r.YearMonth) === yearFilter)
            .forEach((r) => {
                if (!map[r.SchoolId]) map[r.SchoolId] = { site: r.SchoolName, inc: 0, meq: 0 };
                map[r.SchoolId].inc += r.TotalIncomes || 0;
                map[r.SchoolId].meq += r.TotalMEQ || 0;
            });
        return Object.values(map)
            .map((g) => ({
                site: g.site,
                perMEQ: g.meq > 0 ? g.inc / g.meq : 0,
            }))
            .sort((a, b) => b.perMEQ - a.perMEQ);
    }, [incData, yearFilter]);

    const expLeaderboard = useMemo(() => {
        const map = {};
        expData
            .filter((r) => getSchoolYear(r.YearMonth) === yearFilter)
            .forEach((r) => {
                if (!map[r.SchoolId]) map[r.SchoolId] = { site: r.SchoolName, exp: 0, meq: 0 };
                map[r.SchoolId].exp += r.TotalExpenses || 0;
                map[r.SchoolId].meq += r.TotalMEQ || 0;
            });
        return Object.values(map)
            .map((g) => ({
                site: g.site,
                perMEQ: g.meq > 0 ? g.exp / g.meq : 0,
            }))
            .sort((a, b) => b.perMEQ - a.perMEQ);
    }, [expData, yearFilter]);

    const totalIncSites = incLeaderboard.length;
    const totalExpSites = expLeaderboard.length;

    // --- find current site’s rank ---
    const incRank = useMemo(() => {
        const name = sites.find((s) => s.id === siteFilter)?.name;
        const idx = incLeaderboard.findIndex((d) => d.site === name);
        return idx >= 0 ? idx + 1 : null;
    }, [incLeaderboard, siteFilter, sites]);
    const expRank = useMemo(() => {
        const name = sites.find((s) => s.id === siteFilter)?.name;
        const idx = expLeaderboard.findIndex((d) => d.site === name);
        return idx >= 0 ? idx + 1 : null;
    }, [expLeaderboard, siteFilter, sites]);

    // --- monthly detail grid data ---
    const monthlyRows = filteredInc.map((r) => {
        const e = filteredExp.find((x) => x.YearMonth === r.YearMonth) || {};
        return {
            id: r.YearMonth,
            month: r.YearMonth,
            income: r.TotalIncomes || 0,
            incPerMEQ: r.IncomePerMEQ,
            expense: e.TotalExpenses || 0,
            expPerMEQ: e.ExpensePerMEQ,
        };
    });

    const detailCols = [
        { field: "month", headerName: "Month", width: 120 },
        {
            field: "income",
            headerName: "Income ($)",
            width: 140,
            type: "number",
            valueFormatter: ({ value }) =>
                `$${value.toLocaleString()}`,
        },
        {
            field: "incPerMEQ",
            headerName: "Income/MEQ",
            width: 130,
            type: "number",
            valueFormatter: ({ value }) =>
                value != null ? value.toFixed(2) : "",
        },
        {
            field: "expense",
            headerName: "Expense ($)",
            width: 140,
            type: "number",
            valueFormatter: ({ value }) =>
                `$${value.toLocaleString()}`,
        },
        {
            field: "expPerMEQ",
            headerName: "Expense/MEQ",
            width: 130,
            type: "number",
            valueFormatter: ({ value }) =>
                value != null ? value.toFixed(2) : "",
        },
    ];

    return (
        <Box p={3}>
            <Header
                title="Finance Dashboard"
                subtitle="Income vs Expense per MEQ"
            />

            {/* Filters */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        label="Site"
                    >
                        {sites.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>School Year</InputLabel>
                    <Select
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                        label="School Year"
                    >
                        {years.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}
                        label="Month"
                    >
                        <MenuItem value="">All</MenuItem>
                        {months.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Totals */}
            <Box display="grid" gridTemplateColumns="repeat(2,1fr)" gap={2} mb={4}>
                <StatBox
                    title={`$${totalIncome.toLocaleString()}`}
                    subtitle="Total Income"
                    borderColor={colors.greenAccent[500]}
                />
                <StatBox
                    title={`$${totalExpense.toLocaleString()}`}
                    subtitle="Total Expense"
                    borderColor={colors.redAccent[500]}
                />
            </Box>

            {/* Leaderboards */}
            <Box display="flex" gap={2} mb={4}>
                <Box flex={1}>
                    {incRank && (
                        <Box
                            sx={{
                                p: 4,
                                mb: 2,
                                borderRadius: 3,
                                textAlign: "center",
                                boxShadow: `0 0 20px ${colors.greenAccent[500]}`,
                            }}
                        >
                            <Typography variant="h6">
                                {sites.find((s) => s.id === siteFilter)?.name}
                            </Typography>
                            <Typography variant="h1" fontWeight="bold" mb={1}>
                                {incRank}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom>
                                rank out of {totalIncSites}
                            </Typography>
                            <Typography variant="subtitle2" color={colors.greenAccent[700]}>
                                Income/MEQ
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="h6" gutterBottom>
                        🏆 Top 10 Income/MEQ
                    </Typography>
                    <Box component="ol" sx={{
                        pl: 2, m: 0,
                        "& li": {
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.5, px: 1, borderRadius: 1
                        },
                        "& li:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover
                        }
                    }}>
                        {incLeaderboard.slice(0, 10).map((d, i) => (
                            <Typography
                                key={d.site}
                                component="li"
                                sx={{
                                    fontWeight:
                                        d.site === sites.find((s) => s.id === siteFilter)?.name
                                            ? "bold"
                                            : "inherit",
                                }}
                            >
                                <span>{`${i + 1}. ${d.site}`}</span>
                                <span>{d.perMEQ.toFixed(2)}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Box flex={1}>
                    {expRank && (
                        <Box
                            sx={{
                                p: 4,
                                mb: 2,
                                borderRadius: 3,
                                textAlign: "center",
                                boxShadow: `0 0 20px ${colors.redAccent[500]}`,
                            }}
                        >
                            <Typography variant="h6">
                                {sites.find((s) => s.id === siteFilter)?.name}
                            </Typography>
                            <Typography variant="h1" fontWeight="bold" mb={1}>
                                {expRank}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom>
                                rank out of {totalExpSites}
                            </Typography>
                            <Typography variant="subtitle2" color={colors.redAccent[700]}>
                                Expense/MEQ
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="h6" gutterBottom>
                        🏆 Top 10 Expense/MEQ
                    </Typography>
                    <Box component="ol" sx={{
                        pl: 2, m: 0,
                        "& li": {
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.5, px: 1, borderRadius: 1
                        },
                        "& li:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover
                        }
                    }}>
                        {expLeaderboard.slice(0, 10).map((d, i) => (
                            <Typography
                                key={d.site}
                                component="li"
                                sx={{
                                    fontWeight:
                                        d.site === sites.find((s) => s.id === siteFilter)?.name
                                            ? "bold"
                                            : "inherit",
                                }}
                            >
                                <span>{`${i + 1}. ${d.site}`}</span>
                                <span>{d.perMEQ.toFixed(2)}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Monthly Details */}
            <Card>
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        Monthly Details
                    </Typography>
                    <Box sx={{ height: 500, width: "100%" }}>
                        <DataGrid
                            rows={monthlyRows}
                            columns={detailCols}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            pagination
                            disableSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
